<template>
  <div class="audit-log">
    <!-- 查询条件 -->
    <el-card class="search-form" shadow="hover">
      <el-form ref="LogFormRef" :model="LogForm" label-width="100px">
        <el-row :gutter="24">
          <el-col :span="8">
            <el-form-item label="ID">
              <el-input v-model="LogForm.id" placeholder="请输入ID" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="文件名称">
              <el-input
                v-model="LogForm.filename"
                placeholder="请输入文件名称"
              />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="24">
          <el-col :span="8">
            <el-form-item label="创建时间">
              <el-date-picker
                v-model="LogForm.dateRange"
                type="daterange"
                range-separator="至"
                start-placeholder="开始日期"
                end-placeholder="结束日期"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="无感水印状态">
              <el-select
                v-model="LogForm.status"
                placeholder="请选择无感水印状态"
                style="width: 100%"
              >
                <el-option label="通过" value="2" />
                <el-option label="无感水印中" value="1" />
                <el-option label="异常" value="0" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row>
          <el-col :span="24" style="text-align: right">
            <el-button type="primary" @click="getFilesData">
              <el-icon><Search /></el-icon>查询
            </el-button>
            <el-button type="info" @click="reset">
              <el-icon><RefreshLeft /></el-icon>重置
            </el-button>
          </el-col>
        </el-row>
      </el-form>
    </el-card>

    <!-- 数据表格 -->
    <div class="data-table card-mock">
      <el-table
        :data="filteredData"
        style="width: 100%"
        height="100%"
        border
        stripe
        :default-sort="{ prop: 'uploaded_time', order: 'descending' }"
      >
        <el-table-column prop="id" label="文件ID" min-width="100" sortable />
        <el-table-column
          prop="hash_value"
          label="文件名称"
          min-width="240"
          sortable
        >
          <template #default="{ row }">
            <span class="file-name-link" @click="previewFile(row.hash_value)">
              {{ row.hash_value }}
            </span>
          </template>
        </el-table-column>
        <el-table-column
          prop="upload_user_phone"
          label="手机号"
          min-width="120"
          sortable
        />
        <el-table-column
          prop="company"
          label="单位名称"
          min-width="140"
          sortable
        >
          <template #default="{ row }">
            {{ row.company || "-" }}
          </template>
        </el-table-column>
        <el-table-column
          prop="status"
          label="自动无感水印状态"
          min-width="180"
          sortable
        >
          <template #default="{ row }">
            <span :style="{ color: AuditLogStatusMap[row.status]?.color }">
              {{ AuditLogStatusMap[row.status]?.label || "-" }}
            </span>
          </template>
        </el-table-column>

        <el-table-column
          prop="uploaded_time"
          label="上传时间"
          min-width="200"
          sortable
        >
          <template #default="{ row }">
            {{
              row.uploaded_time
                ? dayjs(row.uploaded_time).format("YYYY-MM-DD HH:mm:ss")
                : "-"
            }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="240" fixed="right">
          <template #default="{ row }">
            <el-space>
              <template v-if="isNoAdmin"> </template>
              <template v-else-if="row.is_human_needed">
                <el-button
                  type="primary"
                  size="small"
                  @click="handleReview(row.hash_value, true)"
                >
                  已处理
                </el-button>
                <el-button
                  type="danger"
                  size="small"
                  @click="handleReview(row.hash_value, false)"
                >
                  处理失败
                </el-button>
              </template>
              <el-button
                type="success"
                size="small"
                @click="download(row.hash_value)"
              >
                <el-icon><Download /></el-icon>下载
              </el-button>
              <el-button
                v-if="row.status !== 2"
                type="primary"
                size="small"
                @click="changeResult(row)"
              >
                修改审核结果
              </el-button>
            </el-space>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 预览对话框 -->
    <el-dialog
      v-model="previewDialogVisible"
      title="文件预览"
      width="70%"
      :before-close="handleClose"
    >
      <!-- <video
        v-if="previewUrl && isVideo"
        :src="previewUrl"
        controls
        style="width: 100%"
      ></video> -->
      <img v-if="previewUrl" :src="previewUrl" style="width: 100%" />
    </el-dialog>
    <!-- 修改审核结果 -->
    <AuditResultConfirm
      v-if="changeResultDialogVisible"
      :visible="changeResultDialogVisible"
      :rowData="rowData"
      @close="changeResultClose"
    ></AuditResultConfirm>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from "vue";
import { ElForm, ElMessage, ElLoading, ElInput } from "element-plus";
// import type { ComponentSize } from "element-plus";
import dayjs from "dayjs";
import { AuditLogStatusMap } from "../../enum";
import {
  getFileRecords,
  userSubmitFileForReview,
  adminReview,
  downloadVideoFile,
  downloadFile,
} from "../../api";
import { getUserInfo } from "../../utils";
import AuditResultConfirm from "../components/AuditResultConfirm.vue";

const LogForm = ref({
  id: "",
  filename: "",
  filetype: "",
  unit: "",
  dateRange: [],
  status: "",
});

const filesData = ref([]);
const LogFormRef = ref<typeof ElForm>();
const previewDialogVisible = ref(false);
const previewUrl = ref("");
// const isVideo = ref(false);

const isNoAdmin = computed(() => getUserInfo().role === "user");

const filteredData = computed(() => {
  return filesData.value.filter((item: any) => {
    const matchId =
      !LogForm.value.id || item.id.toString().includes(LogForm.value.id);
    const matchFilename =
      !LogForm.value.filename ||
      item.hash_value
        .toLowerCase()
        .includes(LogForm.value.filename.toLowerCase());
    const matchFiletype =
      !LogForm.value.filetype || item.filetype === LogForm.value.filetype;
    const matchUnit =
      !LogForm.value.unit || item.unit?.includes(LogForm.value.unit);
    const matchStatus =
      !LogForm.value.status || item.status.toString() === LogForm.value.status;

    let matchDate = true;
    if (LogForm.value.dateRange?.length === 2) {
      const itemDate = new Date(item.uploaded_time).getTime();
      const startDate = new Date(LogForm.value.dateRange[0]).getTime();
      const endDate = new Date(LogForm.value.dateRange[1]).getTime();
      matchDate = itemDate >= startDate && itemDate <= endDate;
    }

    return (
      matchId &&
      matchFilename &&
      matchFiletype &&
      matchUnit &&
      matchStatus &&
      matchDate
    );
  });
});

const submitReview = async (filename: string) => {
  const res = await userSubmitFileForReview({ filename });
  if (res?.code === "success") {
    getFilesData();
    ElMessage.success(res.message);
    return;
  }
  ElMessage.error(res?.message || "提交失败");
};

const handleReview = async (filename: string, review_result: boolean) => {
  const res = await adminReview({ filename, review_result });
  if (res?.code === "success") {
    getFilesData();
    ElMessage.success(res.message);
    return;
  }
  ElMessage.error(res?.message || "提交失败");
};

// 方法
const reset = () => {
  LogForm.value = {
    id: "",
    filename: "",
    filetype: "",
    unit: "",
    dateRange: [],
    status: "",
  };
};

async function getFilesData() {
  const res = await getFileRecords();
  if (res?.code === "success") {
    filesData.value = res?.content?.data || [];
  }
}
// 添加下载方法
const download = async (filename: string) => {
  try {
    const videoEnd = ["mp4", "mov"];
    if (videoEnd.some((item) => filename.endsWith(item))) {
      const res = await downloadVideoFile(filename, "download");
      if (res?.code === "success") {
        ElMessage.success(res?.message || "下载成功");
        window.open(res?.content?.download_url, "_blank");
      } else {
        ElMessage.error(res?.message || "下载失败");
      }
    } else {
      const res = await downloadFile(filename);
      if (res?.code === "error") throw new Error(res?.message);
      const blob = new Blob([res.content as string], {
        type: (res.content as { type: string }).type,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(a);
    }
  } catch (error) {
    console.error("下载失败", error);
    ElMessage.error(error?.message || "下载失败");
  }
};

const rowData = ref({});
const changeResultDialogVisible = ref(false);
const changeResultClose = (searchable) => {
  if (searchable) getFilesData();
  changeResultDialogVisible.value = false;
};
const changeResult = async (row) => {
  rowData.value = row;
  changeResultDialogVisible.value = true;
};

// 添加预览方法
const previewFile = async (filename: string) => {
  const loading = ElLoading.service({
    lock: true,
    text: "文件加载中，请稍后",
    background: "rgba(0, 0, 0, 0.7)",
  });

  try {
    const videoEnd = ["mp4", "mov"];
    if (videoEnd.some((item) => filename.endsWith(item))) {
      const res = await downloadVideoFile(filename, "preview");
      loading.close();
      if (res?.code === "success") {
        ElMessage.success(res?.message || "预览成功");
        window.open(res?.content?.url, "_blank");
      } else {
        ElMessage.error(res?.message || "预览失败");
      }
    } else {
      const res = await downloadFile(filename);
      if (res?.code === "error") throw new Error(res?.message);
      if (!res.content) throw new Error("预览的文件内容为空或不可用");
      previewUrl.value = URL.createObjectURL(res.content as Blob);
      loading.close();
      previewDialogVisible.value = true;
    }
  } catch (error) {
    loading.close();
    console.error("预览失败", error);
    ElMessage.error(error?.message || "该文件未生成水印文件");
  }
};

const handleClose = () => {
  if (previewUrl.value) {
    URL.revokeObjectURL(previewUrl.value);
    previewUrl.value = "";
  }
  previewDialogVisible.value = false;
};

onMounted(() => {
  getFilesData();
});
</script>

<style lang="scss" scoped>
.audit-log {
  height: 100%;
  .search-form {
    .el-form-item {
      margin-bottom: 18px;
    }
  }

  .data-table {
    height: calc(100% - 194px);
    margin-top: 20px;
    padding: 20px;
    background: #fff;
    border: 1px solid #e3e3e3;
    border-radius: 4px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    .el-table {
      flex: 1;
    }

    .file-name-link {
      color: var(--el-color-primary);
      cursor: pointer;

      &:hover {
        color: var(--el-color-primary-light-3);
      }
    }
  }
}
</style>
